/**
 * Home — the project list, the templates, and the honest state of local storage.
 *
 * Everything destructive here is reversible: deleting moves a project to a trash that keeps
 * it for thirty days and says how many are left, because the one thing this app must never
 * do is lose a recording to a mis-tap.
 */

import { h, list, onCleanup } from '../../core/dom';
import { computed, effect, signal } from '../../core/signal';
import { icon, neonButton, chip } from '../components/controls';
import { confirmDialog, emptyState } from '../components/sheet';
import { downloadFile, shareContent } from '../../platform/share';
import { isStandalone } from '../../platform/standalone';
import type { ProjectSummary } from '../../model/types';
import {
  STARTER_TEMPLATES,
  adoptProject,
  createProjectFromTemplate,
  daysUntilPurge,
  notify,
  openProject,
  project,
  projectStore,
  projects,
  refreshStorageReport,
  reportFailure,
  settings,
  storageReport,
  trashedProjects,
  updateSettings,
  type StarterTemplate,
} from '../../app/state';
import { importDroppedFiles, looksImportable } from '../../app/import';
import { navigate } from '../../app/router';
import {
  callout,
  formatBytes,
  formatRelativeDate,
  presentMenu,
  presentPrompt,
  screenBody,
  screenHeader,
  section,
} from '../../app/shell';

/** Captured from `beforeinstallprompt` where the browser fires it; never on iOS. */
const installPrompt = signal<{ prompt: () => Promise<void> } | null>(null);
if (typeof window !== 'undefined') {
  window.addEventListener('beforeinstallprompt', (event: Event) => {
    event.preventDefault();
    installPrompt.value = event as unknown as { prompt: () => Promise<void> };
  });
}

const busy = signal<string | null>(null);

async function startTemplate(template: StarterTemplate): Promise<void> {
  if (busy.peek() !== null) return;
  busy.value = template.id;
  try {
    const created = await createProjectFromTemplate(template);
    await adoptProject(created);
    await navigate({ name: 'board', projectID: created.id });
  } catch (error) {
    reportFailure('That template could not be created', error);
  } finally {
    busy.value = null;
  }
}

async function openAndShow(id: string): Promise<void> {
  if (await openProject(id)) await navigate({ name: 'board', projectID: id });
}

function projectMenu(summary: ProjectSummary): void {
  presentMenu(summary.name, [
    { title: 'Open', symbol: 'play.fill', onSelect: () => void openAndShow(summary.id) },
    {
      title: 'Rename',
      symbol: 'slider.horizontal.3',
      onSelect: () =>
        presentPrompt({
          title: 'Rename project',
          value: summary.name,
          onConfirm: (name) => {
            void projectStore()
              .rename(summary.id, name)
              .then(() => notify('Renamed'))
              .catch((error: unknown) => reportFailure('Rename failed', error));
          },
        }),
    },
    {
      title: 'Duplicate',
      symbol: 'square.grid.3x3.fill',
      detail: 'Shares the same recordings',
      onSelect: () => {
        void projectStore()
          .duplicate(summary.id, `${summary.name} copy`)
          .then(() => notify('Duplicated'))
          .catch((error: unknown) => reportFailure('Duplicate failed', error));
      },
    },
    {
      title: summary.isArchived ? 'Unarchive' : 'Archive',
      symbol: 'folder.fill',
      onSelect: () => {
        void projectStore()
          .setArchived(summary.id, !summary.isArchived)
          .catch((error: unknown) => reportFailure('That did not work', error));
      },
    },
    {
      title: 'Export backup',
      symbol: 'square.and.arrow.up',
      detail: '.padlab package',
      onSelect: () => void exportBackup(summary),
    },
    {
      title: 'Delete',
      symbol: 'trash.fill',
      destructive: true,
      detail: 'Recoverable for 30 days',
      onSelect: () => {
        confirmDialog({
          title: `Delete “${summary.name}”?`,
          message: 'It moves to Recently Deleted and stays recoverable for 30 days. Nothing is erased yet.',
          confirmTitle: 'Delete',
          destructive: true,
          onConfirm: () => {
            void projectStore()
              .delete(summary.id)
              .then(() => notify('Moved to Recently Deleted'))
              .catch((error: unknown) => reportFailure('Delete failed', error));
          },
        }).present();
      },
    },
  ]);
}

async function exportBackup(summary: ProjectSummary): Promise<void> {
  try {
    const blob = await projectStore().exportPackage(summary.id);
    const filename = projectStore().packageFileName(summary.name);
    const file = new File([blob], filename, { type: 'application/octet-stream' });
    const result = await shareContent({ title: summary.name, files: [file] });
    if (result.outcome === 'downloaded') notify('Saved to your downloads', filename);
    else if (result.outcome === 'shared') notify('Shared');
  } catch (error) {
    reportFailure('Backup failed', error);
  }
}

function projectRow(summary: ProjectSummary): HTMLElement {
  const open = computed(() => project.value?.id === summary.id);
  const openKicker = h('span', { class: 'kicker project-row__open-kicker' }, 'Open');
  const row = h(
    'div',
    { class: 'project-row' },
    h(
      'button',
      { class: 'project-open', type: 'button', onclick: () => void openAndShow(summary.id) },
      h(
        'span',
        { class: 'project-main' },
        // The open row says so in a word rather than by being tinted a different colour.
        // A kicker survives greyscale, and a screen reader reads it out; a tint does neither.
        openKicker,
        h('span', { class: 'project-name truncate' }, summary.name),
        h(
          'span',
          { class: 'project-meta' },
          `${summary.padCount} pad${summary.padCount === 1 ? '' : 's'} · ${summary.assetCount} sound${summary.assetCount === 1 ? '' : 's'} · ${Math.round(summary.tempo)} BPM`,
        ),
      ),
      h(
        'span',
        { class: 'project-side' },
        h('span', { class: 'kicker' }, formatRelativeDate(summary.modifiedAt)),
        h('span', { class: 'project-size numeric' }, formatBytes(summary.byteSize)),
      ),
    ),
    h(
      'button',
      { class: 'project-more', type: 'button', 'aria-label': `Actions for ${summary.name}`, onclick: () => projectMenu(summary) },
      icon('slider.horizontal.3', { size: 18 }),
    ),
  );
  const stop = effect(() => {
    const isOpen = open.value;
    row.classList.toggle('project-row--open', isOpen);
    openKicker.hidden = !isOpen;
    // The word "Open" is inside the button's accessible name and survives greyscale; this is
    // the programmatic half of the same statement, for anything navigating by state.
    const button = row.querySelector('.project-open');
    if (isOpen) button?.setAttribute('aria-current', 'true');
    else button?.removeAttribute('aria-current');
  });
  onCleanup(row, stop);
  return row;
}

/**
 * One row of the starter list.
 *
 * It used to be a floating rounded card in a grid of five identical floating rounded cards,
 * each with a glyph, a bold title and a grey line under it. That shape is the giveaway of a
 * generated interface, and it made five equal-weight boxes out of what is really a short list
 * of choices. It is now a row on a single divided plate: a glyph, a kicker, a name and a
 * summary, told apart by type and by the symbol rather than by having a box drawn round each.
 *
 * The heading and paragraph elements went with it. A `<button>` may only contain phrasing
 * content, so `<h3>` and `<p>` inside one were invalid markup; the button's accessible name is
 * the same text either way.
 */
function templateCard(template: StarterTemplate): HTMLElement {
  const card = h(
    'button',
    {
      class: 'template-card',
      type: 'button',
      onclick: () => void startTemplate(template),
    },
    icon(template.symbol, { size: 20 }),
    h(
      'span',
      { class: 'template-text' },
      h('span', { class: 'kicker' }, template.kicker),
      h('span', { class: 'template-name' }, template.name),
      h('span', { class: 'template-summary' }, template.summary),
    ),
    h('span', { class: 'template-go', 'aria-hidden': 'true' }, icon('chevron.right', { size: 15 })),
  ) as HTMLButtonElement;
  const stop = effect(() => {
    const working = busy.value === template.id;
    card.classList.toggle('template-card--busy', working);
    card.disabled = busy.value !== null;
  });
  onCleanup(card, stop);
  return card;
}

function storagePanel(): HTMLElement {
  const body = h('div', { class: 'storage-summary' });
  const stop = effect(() => {
    const report = storageReport.value;
    body.replaceChildren();
    if (report === null) {
      body.appendChild(h('p', { class: 'muted' }, 'Measuring local storage…'));
      return;
    }
    const used = report.projectsBytes + report.mediaBytes + report.peaksBytes + report.rendersBytes + report.recordingsBytes;
    const total = report.totalBytes > 0 ? report.totalBytes : used + report.availableBytes;
    // Flat segments sized by their share, separated by a gap. `reclaimable` is the only one
    // that gets an accent, because it is the only one the user can do anything about.
    const segments: { label: string; bytes: number; colour: string }[] = [
      { label: 'audio', bytes: report.mediaBytes, colour: 'var(--tone-kicker)' },
      { label: 'projects', bytes: report.projectsBytes, colour: 'var(--tone-tertiary)' },
      { label: 'reclaimable', bytes: report.reclaimableBytes, colour: 'var(--accent-acid)' },
    ];
    const denominator = total > 0 ? total : Math.max(used, 1);
    const bar = h(
      'div',
      { class: 'storage-bar' },
      ...segments
        .filter((segment) => segment.bytes > 0)
        .map((segment) =>
          h('div', {
            class: 'storage-seg',
            style: { width: `${((segment.bytes / denominator) * 100).toFixed(2)}%`, '--seg-colour': segment.colour },
          }),
        ),
      h('div', { class: 'storage-seg storage-seg--free' }),
    );
    bar.setAttribute('role', 'img');
    bar.setAttribute(
      'aria-label',
      `${segments.map((segment) => `${formatBytes(segment.bytes)} ${segment.label}`).join(', ')}, ${formatBytes(Math.max(0, denominator - used))} free`,
    );
    body.append(
      bar,
      h(
        'div',
        { class: 'storage-legend' },
        ...segments.map((segment) =>
          h(
            'span',
            null,
            h('i', { class: 'storage-swatch', style: { '--seg-colour': segment.colour }, 'aria-hidden': 'true' }),
            `${formatBytes(segment.bytes)} ${segment.label}`,
          ),
        ),
        h('span', { class: 'muted' }, total > 0 ? `${formatBytes(total)} available` : 'Quota unknown'),
      ),
      neonButton({ title: 'Manage storage', style: 'ghost', onPress: () => void navigate({ name: 'settings' }) }).el,
    );
  });
  onCleanup(body, stop);
  void refreshStorageReport();
  return section('Storage', body);
}

/**
 * An ordinary card, and deliberately the quietest thing on the screen. It is advice, not the
 * product: the projects below it are what the user came for, so it gets the same hairline and
 * the same raised fill as every other card and says what it is with a kicker and a glyph.
 * It used to be drawn with a magenta border over a tinted fill, which made the one panel the
 * user is most likely to ignore the loudest panel on Home.
 */
function installCard(title: string, message: string, ...actions: Node[]): HTMLElement {
  return h(
    'div',
    { class: 'callout install-card' },
    h(
      'div',
      { class: 'install-card-head' },
      icon('square.and.arrow.up', { size: 20 }),
      h('p', { class: 'kicker' }, 'Install'),
    ),
    h('h3', { class: 'callout-title' }, title),
    h('p', { class: 'callout-message' }, message),
    h('div', { class: 'callout-actions' }, ...actions),
  );
}

function installPanel(): HTMLElement | null {
  if (isStandalone.peek()) return null;
  // One time, not every time. `installGuideSeen` was written by the "Show me how" button and
  // read nowhere, so the card sat at the top of Home on every visit for the life of the
  // install — which is precisely the "annoying" the brief rules out. The browser's own
  // install prompt is different: it only exists when the browser has decided to offer one,
  // so it is still worth showing.
  if (settings.peek().installGuideSeen && installPrompt.peek() === null) return null;
  const canPrompt = computed(() => installPrompt.value !== null);
  const host = h('div', { class: 'install-panel' });
  const stop = effect(() => {
    host.replaceChildren();
    // One primary button either way: the browser's own prompt where it exists, the Safari
    // walkthrough where it does not.
    host.appendChild(
      canPrompt.value
        ? installCard(
            'Add PadLab to your Home Screen',
            'Installed, it launches full-screen with no browser chrome and keeps working offline.',
            neonButton({
              title: 'Install PadLab',
              style: 'primary',
              onPress: () => {
                void installPrompt.peek()?.prompt();
              },
            }).el,
          )
        : installCard(
            'Add PadLab to your Home Screen',
            'In Safari, tap the Share button, then “Add to Home Screen”. PadLab then launches full-screen and works with no connection.',
            neonButton({
              title: 'Show me how',
              style: 'primary',
              onPress: () => {
                updateSettings({ installGuideSeen: true });
                void navigate({ name: 'settings' });
              },
            }).el,
            neonButton({
              title: 'Not now',
              style: 'secondary',
              onPress: () => updateSettings({ installGuideSeen: true }),
            }).el,
          ),
    );
  });
  onCleanup(host, stop);
  return host;
}

function trashPanel(): HTMLElement {
  const host = h('div', { class: 'trash-list' });
  list(
    host,
    trashedProjects,
    (summary) => summary.id,
    (summary) =>
      h(
        'div',
        { class: 'trash-row' },
        h(
          'div',
          { class: 'trash-main' },
          h('span', { class: 'truncate' }, summary.name),
          h(
            'span',
            { class: 'kicker' },
            summary.deletedAt == null ? 'Deleted' : `${daysUntilPurge(summary.deletedAt)} days left`,
          ),
        ),
        neonButton({
          title: 'Restore',
          style: 'secondary',
          onPress: () => {
            void projectStore()
              .restore(summary.id)
              .then(() => notify('Restored'))
              .catch((error: unknown) => reportFailure('Restore failed', error));
          },
        }).el,
        neonButton({
          title: 'Delete now',
          style: 'destructive',
          onPress: () => {
            confirmDialog({
              title: 'Delete permanently?',
              message: 'This erases the project and any recordings no other project uses. It cannot be undone.',
              confirmTitle: 'Delete permanently',
              destructive: true,
              onConfirm: () => {
                void projectStore()
                  .permanentlyDelete(summary.id)
                  .then(() => {
                    notify('Deleted');
                    void refreshStorageReport();
                  })
                  .catch((error: unknown) => reportFailure('Delete failed', error));
              },
            }).present();
          },
        }).el,
      ),
  );
  const wrapper = h('div', null, host);
  const stop = effect(() => {
    wrapper.hidden = trashedProjects.value.length === 0;
  });
  onCleanup(wrapper, stop);
  return section('Recently deleted', wrapper);
}

async function importPackage(file: File): Promise<void> {
  try {
    const imported = await projectStore().importPackage(file);
    notify('Project imported', imported.name);
    await openAndShow(imported.id);
  } catch (error) {
    reportFailure('That backup could not be opened', error);
  }
}

/**
 * Takes whatever the user picked, and works out what it is rather than assuming.
 *
 * This entry point used to hand every file straight to `importPackage`, so choosing an MP3
 * ran it through the project-package reader and answered "That backup could not be opened" —
 * a true sentence about the wrong question, and one that reads as "PadLab will not take your
 * music". Nothing on screen said Import meant *backups only*, and the picker's
 * `application/octet-stream` filter let audio through anyway.
 *
 * Audio and video now go to the sound importer, which is what the person meant. A sound needs
 * a project to live in, so if none is open one is created first rather than failing with
 * "Open a project before importing a sound" at the end of a file picker.
 */
async function importAnyFile(file: File): Promise<void> {
  if (!looksImportable(file)) {
    await importPackage(file);
    return;
  }

  if (project.peek() === null) {
    try {
      const created = await createProjectFromTemplate(STARTER_TEMPLATES[0]!, 'Imported sounds');
      await adoptProject(created);
    } catch (error) {
      reportFailure('That sound could not be imported', error);
      return;
    }
  }

  const outcome = await importDroppedFiles([file], 'picker');
  const projectID = project.peek()?.id;
  if (outcome.imported.length > 0 && projectID !== undefined) {
    await navigate({ name: 'library', projectID });
  }
}

export function homeScreen(): HTMLElement {
  const showArchived = signal(false);
  const visible = computed(() =>
    projects.value.filter((summary) => (showArchived.value ? summary.isArchived : !summary.isArchived)),
  );

  const fileInput = h('input', {
    type: 'file',
    // Backups *and* audio: this one control is what people reach for to get anything into
    // PadLab, and a filter that hides their music makes the app look like it cannot take it.
    accept: '.padlab,application/octet-stream,audio/*,video/*',
    // `visually-hidden` is a 1px clip, not `display: none`: the input stays in the tab order
    // and in the accessibility tree, so a keyboard or VoiceOver user reached an unnamed
    // control between the project list and the trash. `hidden` takes it out of both, and the
    // Import button that opens it is what is actually reachable.
    hidden: 'hidden',
    tabindex: '-1',
    'aria-hidden': 'true',
    onchange: (event: Event) => {
      const input = event.target as HTMLInputElement;
      const file = input.files?.[0];
      input.value = '';
      if (file !== undefined) void importAnyFile(file);
    },
  }) as HTMLInputElement;

  const listHost = h('div', { class: 'project-list' });
  list(listHost, visible, (summary) => summary.id, projectRow, (element, summary) => {
    const name = element.querySelector('.project-name');
    if (name !== null && name.textContent !== summary.name) name.textContent = summary.name;
  });

  const emptyHost = h('div');
  const stopEmpty = effect(() => {
    emptyHost.replaceChildren();
    if (visible.value.length > 0) return;
    emptyHost.appendChild(
      emptyState({
        symbol: 'folder.fill',
        title: showArchived.value ? 'Nothing archived' : 'No projects yet',
        message: showArchived.value
          ? 'Archived projects stay out of the way here until you need them.'
          : 'Pick a starter board below and you will be playing pads in a couple of seconds.',
        ...(showArchived.value
          ? {}
          : {
              actionTitle: 'Start a blank board',
              onAction: () => void startTemplate(STARTER_TEMPLATES[0]!),
            }),
      }),
    );
  });
  onCleanup(emptyHost, stopEmpty);

  return h(
    'div',
    { class: 'screen screen--home' },
    screenHeader({
      kicker: 'PadLab',
      title: 'Projects',
      showsBack: false,
      actions: [
        neonButton({ title: 'Import', style: 'ghost', onPress: () => fileInput.click() }).el,
        neonButton({ title: 'Settings', style: 'ghost', onPress: () => void navigate({ name: 'settings' }) }).el,
      ],
    }),
    screenBody(
      fileInput,
      installPanel(),
      section(
        'Recent',
        h(
          'div',
          { class: 'chip-row' },
          // "Active" is where this rail starts; acid is for a filter the user applied.
          chip({ title: 'Active', resting: true, selected: computed(() => !showArchived.value), onPress: () => (showArchived.value = false) }).el,
          chip({ title: 'Archived', selected: showArchived, onPress: () => (showArchived.value = true) }).el,
        ),
        listHost,
        emptyHost,
      ),
      section(
        'Start something',
        h('div', { class: 'template-grid' }, ...STARTER_TEMPLATES.map(templateCard)),
      ),
      trashPanel(),
      storagePanel(),
      settings.peek().onboardingComplete
        ? null
        : callout(
            'First time here',
            'Take the two-minute tour',
            'It shows how pads, recording and beats fit together. It never turns on your microphone.',
            'accent',
            h(
              'div',
              { class: 'callout-actions' },
              neonButton({ title: 'Start the tour', style: 'primary', onPress: () => void navigate({ name: 'onboarding' }) }).el,
            ),
          ),
      h(
        'div',
        { class: 'home-footer' },
        h('p', { class: 'kicker' }, 'Everything stays on this device'),
        h(
          'p',
          { class: 'muted' },
          'Projects, recordings and packs are stored locally and work offline. Nothing is uploaded unless you share it yourself.',
        ),
        neonButton({
          title: 'Download every project',
          style: 'ghost',
          onPress: () => {
            void (async (): Promise<void> => {
              try {
                for (const summary of projects.peek()) {
                  const blob = await projectStore().exportPackage(summary.id);
                  downloadFile(blob, projectStore().packageFileName(summary.name));
                }
                notify('Exported', 'One .padlab file per project.');
              } catch (error) {
                reportFailure('Export failed', error);
              }
            })();
          },
        }).el,
      ),
    ),
  );
}
